// The welcome tour shown on a company's dashboard the first time someone opens it (and from Settings to replay it).
// The same file is in frontend/src/components/tour, premium/src/features/tour and enterprices/src/features/tour:
// keep the three copies identical.
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft, ArrowRight, ChartColumn, Bell, Bot, CircleCheck, Headphones, KeyRound, LayoutDashboard, Megaphone,
  MessageCircle, Phone, PhoneCall, Settings, ShieldCheck, Sparkles, UserRound, Users, Workflow, X, Zap,
} from 'lucide-react';
import './welcome-tour.css';

const STEPS = 6;

/** A small TeleCalX window: the sidebar (one item highlighted) and the given content. */
function Mock({ active, children }) {
  const items = [['dashboard', 'Dashboard', LayoutDashboard], ['numbers', 'Phone Numbers', Phone], ['voice', 'Call & Voice', PhoneCall],
    ['ai', 'AI Agents', Bot], ['team', 'Team', Users], ['settings', 'Settings', Settings]];
  return (
    <div className="wt-mock">
      <aside>
        <b className="wt-mock-brand">TeleCal<span>X</span></b>
        {items.map(([k, label, Icon]) => <i key={k} className={k === active ? 'on' : ''}><Icon size={11} />{label}</i>)}
      </aside>
      <div className="wt-mock-main">{children}</div>
    </div>
  );
}

function Chart() {
  return (
    <svg className="wt-chart" viewBox="0 0 200 60" preserveAspectRatio="none" aria-hidden="true">
      <path className="area" d="M0 50 L25 42 L50 46 L75 30 L100 36 L125 22 L150 28 L175 12 L200 16 L200 60 L0 60 Z" />
      <path d="M0 50 L25 42 L50 46 L75 30 L100 36 L125 22 L150 28 L175 12 L200 16" />
    </svg>
  );
}

function Stats() {
  return (
    <div className="wt-stats">{[['Total Calls', PhoneCall], ['Active Numbers', Phone], ['AI Conversations', Bot], ['Team Members', Users]].map(([l, Icon]) => (
      <span key={l}><Icon size={11} /><small>{l}</small><b /></span>
    ))}</div>
  );
}

function Callout({ icon: Icon, tone = 'green', title, text, className }) {
  return (
    <div className={`wt-callout ${className}`}><span className={`wt-ic ${tone}`}><Icon size={16} /></span><div><b>{title}</b><small>{text}</small></div></div>
  );
}

function Robot({ say }) {
  return (
    <div className="wt-robot" aria-hidden="true">
      {say && <p className="wt-bubble">{say}</p>}
      <span className="wt-robot-head"><Bot size={54} strokeWidth={1.6} /></span>
    </div>
  );
}

function Rows({ cols, rows }) {
  return (
    <div className="wt-rows">
      <div className="wt-rows-head">{cols.map((c) => <small key={c}>{c}</small>)}</div>
      {rows.map((r) => <div key={r[0]} className="wt-rows-row">{r.map((v, i) => (i === r.length - 1 ? <i key={i}>{v}</i> : <span key={i}>{v}</span>))}</div>)}
    </div>
  );
}

/**
 * Six steps over the dashboard. `has(feature)` says whether the company's plan includes a feature (Feature Access
 * Control), so only what they can use is introduced. `onClose('finish' | 'skip')` saves it and closes the tour.
 */
export default function WelcomeTour({ companyName, userName, logoSrc, has = () => true, onClose }) {
  const [step, setStep] = useState(1);
  const [logoOk, setLogoOk] = useState(true);
  const go = useCallback((n) => setStep(Math.min(STEPS, Math.max(1, n))), []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose('skip');
      else if (e.key === 'ArrowRight') go(step + 1);
      else if (e.key === 'ArrowLeft') go(step - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, go, onClose]);

  const ai = has('ai_receptionist') || has('ai_agents');
  const messaging = has('whatsapp') || has('sms');
  const only = (list) => list.filter(([, ok = true]) => ok).map(([text]) => text);

  const content = {
    1: {
      title: <>Welcome to<br /><em>TeleCalX! 🎉</em></>,
      text: `Your all-in-one business calling platform is now ready${companyName ? ` for ${companyName}` : ''}. Let's take a quick tour to help you get started.`,
      points: ['Set up your business communication', ...(ai ? ['Explore powerful AI features'] : []), 'Manage your team and settings'],
      art: (
        <div className="wt-art">
          <div className="wt-laptop"><Mock active="dashboard"><Stats /><div className="wt-panel"><small>Call Activity</small><Chart /></div></Mock></div>
          <Robot say="I'll show you around your TeleCalX dashboard!" />
        </div>
      ),
    },
    2: {
      title: <>Explore Your<br /><em>Dashboard</em></>,
      text: 'Get a complete overview of your business communication at a glance.',
      points: only([['View key metrics: total calls, active numbers and team members'], ['Monitor call activity and analytics in real time'],
        ['Track AI conversations and team performance', ai], ['See recent activity across your business']]),
      art: (
        <div className="wt-art">
          <Mock active="dashboard"><Stats /><div className="wt-split"><div className="wt-panel"><small>Call Activity</small><Chart /></div>
            <div className="wt-panel"><small>Recent Activity</small><i className="wt-line" /><i className="wt-line" /><i className="wt-line short" /></div></div></Mock>
          <Callout icon={LayoutDashboard} title="Main Navigation" text="Every feature from the menu" className="tl" />
          <Callout icon={Zap} tone="purple" title="Quick Actions" text="Add numbers, invite your team and more" className="tr" />
          <Callout icon={ChartColumn} tone="blue" title="Key Metrics" text="Your business activity" className="bl" />
          <Callout icon={PhoneCall} tone="purple" title="Call Analytics" text="Call trends and growth" className="br" />
        </div>
      ),
    },
    3: {
      title: <>Manage Your<br /><em>Business Communication</em></>,
      text: 'Set up and manage your business calling with ease.',
      points: only([['Buy and manage phone numbers (DID)', has('business_phone_number')], ['Configure call routing and IVR call flows', has('call_flows')],
        ['Share calls across your team with call queues', has('call_queues')], ['Create campaigns and templates', has('campaigns')],
        ['Manage WhatsApp & SMS', messaging], ['Review call history and recordings', has('call_history') || has('call_recording')]]),
      art: (
        <div className="wt-art">
          <Mock active="numbers"><div className="wt-title-row"><b>Phone Numbers (DID)</b><i>+ Add Number</i></div>
            <Rows cols={['Number', 'Type', 'Status']} rows={[['+91 •••• •• 5678', 'Local', 'Active'], ['+91 •••• •• 0123', 'Toll-free', 'Active'], ['+91 •••• •• 5432', 'Local', 'Active']]} /></Mock>
          <Callout icon={Phone} title="Phone Numbers" text="Buy, manage and configure your numbers" className="tr" />
          <Callout icon={Workflow} tone="purple" title="Call & Voice Services" text="IVR, call routing and more" className="bl" />
          {messaging && <Callout icon={MessageCircle} title="WhatsApp & SMS" text="Campaigns and templates" className="br" />}
        </div>
      ),
    },
    4: ai ? {
      title: <>Powerful AI Features<br /><em>for Your Business</em></>,
      text: 'Let AI handle your calls, messages and customer queries 24/7.',
      points: only([['AI Receptionist for instant call handling', has('ai_receptionist')], ['Create and manage AI agents for sales, support and bookings', has('ai_agents')],
        ['Automate customer conversations'], ['Smart call routing to the right person', has('call_flows') || has('call_queues')]]),
      art: (
        <div className="wt-art">
          <Mock active="ai"><div className="wt-title-row"><b>AI Agents</b><i>+ Create Agent</i></div>
            <Rows cols={['Agent', 'Type', 'Status']} rows={[['Main Receptionist', 'Voice AI', 'Active'], ['Sales Assistant', 'Voice AI', 'Active'], ['Support Agent', 'Voice AI', 'Active']]} /></Mock>
          <Callout icon={Bot} tone="purple" title="AI Agents" text="Create and manage your AI receptionists" className="tr" />
          <Callout icon={Workflow} tone="blue" title="Smart Call Handling" text="Answer, qualify and route calls" className="bl" />
          <Callout icon={Headphones} tone="purple" title="24/7 Customer Support" text="Never miss a customer conversation" className="br" />
        </div>
      ),
    } : {
      title: <>Powerful AI Features<br /><em>When You're Ready</em></>,
      text: 'AI receptionists and AI agents answer, qualify and route your calls 24/7. They are part of Business Standard and above.',
      points: ['Answer every call instantly with an AI receptionist', 'Automate customer conversations', 'Upgrade any time from Billing & Plan'],
      badge: 'Available on higher plans',
      art: (
        <div className="wt-art">
          <Mock active="ai"><div className="wt-title-row"><b>AI Agents</b><i>Upgrade</i></div>
            <div className="wt-locked"><Sparkles size={22} /><b>AI features</b><small>Available on Business Standard and above</small></div></Mock>
          <Robot />
        </div>
      ),
    },
    5: {
      title: <>Settings &amp; Security<br /><em>Keep Your Account Safe</em></>,
      text: 'Manage your profile, team members and security settings.',
      points: only([['Update your profile and account settings'], ['Manage team members and their roles', has('team')],
        ['Turn on two-factor authentication'], ['Choose your notifications and preferences']]),
      art: (
        <div className="wt-art">
          <Mock active="settings"><div className="wt-split">
            <div className="wt-panel"><small><UserRound size={11} /> Profile Information</small><i className="wt-line" /><i className="wt-line" /><i className="wt-line short" /></div>
            <div className="wt-panel center"><small><ShieldCheck size={11} /> Two-Factor Authentication</small><KeyRound size={22} /><i className="wt-pill">Enable 2FA</i></div></div></Mock>
          {has('team') && <Callout icon={Users} title="Team Management" text="Add team members and control access" className="bl" />}
          <Callout icon={Bell} tone="amber" title="Notification Preferences" text="Choose how you get updates" className="br" />
        </div>
      ),
    },
    6: {
      title: <>You're All Set! <em>🚀</em></>,
      text: 'Welcome to the future of business calling. Explore TeleCalX and grow your business with powerful communication tools.',
      points: only([['Dashboard insights'], ['Business communication setup'], ['AI agents and automation', ai], ['Team management', has('team')], ['Security and account settings']]),
      art: (
        <div className="wt-art">
          <div className="wt-orbit">
            {[[Phone, 'green', 'a'], [MessageCircle, 'green', 'b'], [ChartColumn, 'blue', 'c'], [Users, 'blue', 'd'], [Settings, 'blue', 'e'], [Megaphone, 'purple', 'f']]
              .map(([Icon, tone, k]) => <span key={k} className={`wt-float ${tone} ${k}`}><Icon size={22} /></span>)}
          </div>
          <Robot say="Welcome to the future of business calling! Let's grow together! 💚" />
        </div>
      ),
    },
  }[step];

  const first = (userName || '').trim().charAt(0).toUpperCase() || '?';
  return (
    <div className="wt-backdrop">
      <div className="wt-dialog" role="dialog" aria-modal="true" aria-labelledby="wt-title">
        <header className="wt-head">
          {logoOk && logoSrc
            ? <img className="wt-logo" src={logoSrc} alt="TeleCalX" onError={() => setLogoOk(false)} />
            : <b className="wt-wordmark">TeleCal<span>X</span></b>}
          <div className="wt-who"><span><b>Welcome to TeleCalX</b><small>{companyName}</small></span><i>{first}</i></div>
          <button type="button" className="wt-close" aria-label="Close the tour" onClick={() => onClose('skip')}><X size={18} /></button>
        </header>
        <div className="wt-body" key={step}>
          <section className="wt-copy">
            <div className="wt-progress" aria-hidden="true">{Array.from({ length: STEPS }, (_, i) => <i key={i} className={i + 1 < step ? 'done' : i + 1 === step ? 'on' : ''} />)}</div>
            <small className="wt-step">Step {step} of {STEPS}</small>
            <h2 id="wt-title">{content.title}</h2>
            {content.badge && <span className="wt-badge"><Sparkles size={12} />{content.badge}</span>}
            <p>{content.text}</p>
            <ul>{content.points.map((pt) => <li key={pt}><CircleCheck size={16} />{pt}</li>)}</ul>
            <div className="wt-actions">
              {step === 1 && <button type="button" className="wt-primary" onClick={() => go(2)}>Let's Get Started <ArrowRight size={16} /></button>}
              {step > 1 && step < STEPS && (
                <>
                  <button type="button" className="wt-secondary" onClick={() => go(step - 1)}><ArrowLeft size={16} /> Back</button>
                  <button type="button" className="wt-primary" onClick={() => go(step + 1)}>Next <ArrowRight size={16} /></button>
                </>
              )}
              {step === STEPS && (
                <>
                  <button type="button" className="wt-primary" onClick={() => onClose('finish')}>Go to Dashboard <ArrowRight size={16} /></button>
                  <button type="button" className="wt-secondary" onClick={() => onClose('skip')}>Skip Tour</button>
                </>
              )}
            </div>
          </section>
          <section className="wt-visual" aria-hidden="true">{content.art}</section>
        </div>
        {step < STEPS && <button type="button" className="wt-skip" onClick={() => onClose('skip')}>Skip Tour</button>}
      </div>
    </div>
  );
}
